import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserUpdateUserTotpDevice } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes } from "../../../../common/constants";
import { AgentUserProfile, ClientUserProfile, UpdateUserTotpDevice, UserTotpDevice } from "../../types";

interface Props {
  user: AgentUserProfile | ClientUserProfile;
  totpDevice?: UserTotpDevice;
  onFormCancel: () => void;
}

export const UpdateUserTotpDeviceForm = ({ user, totpDevice, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UpdateUserTotpDevice>();

  const updateUserTotpDevice = useUserUpdateUserTotpDevice();

  useEffect(() => {
    if (totpDevice) {
      form.setFieldsValue({
        optimisticLockVersion: totpDevice.optimisticLockVersion,
        name: totpDevice.name,
      });
    }
  }, [totpDevice, form]);

  const handleFormSubmit = () => {
    if (totpDevice) {
      form
        .validateFields()
        .then((values) => {
          updateUserTotpDevice.mutate(
            { id1: user.id, id2: totpDevice.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "user.totpDevice.attrs", error.violations),
            },
          );
        })
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={!!totpDevice}
      title={t("user.totpDevice.titles.update")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateUserTotpDevice.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="updateUserTotpDeviceForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="name"
          label={t("user.totpDevice.attrs.name")}
          rules={[validations.notNull, validations.size(1, 64)]}
        >
          <Input placeholder={t("user.totpDevice.helpers.nameHint")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
